import {useEffect} from 'react'
import type {JSX} from 'react'
import Head from 'next/head'
import {useRouter} from 'next/router'
import {env} from '@/services/env/client'
import {isFibrPilotPage} from '@/services/fibr'
import {
	FIBR_CLIENT_SDK_ID,
	FIBR_DNS_PREFETCH_LINK_ID,
	FIBR_INLINE_SCRIPT,
	FIBR_INLINE_SCRIPT_ID,
	FIBR_PRECONNECT_LINK_ID,
	FIBR_PRECONNECT_URL,
} from '@/services/fibr/scripts'

const FIBR_TEMP_STYLE_IDS = ['fibr-async-img-style', 'fibr-style']

function appendLink(id: string, rel: 'preconnect' | 'dns-prefetch'): void {
	if (document.getElementById(id)) return

	const link = document.createElement('link')
	link.dataset.testid = id
	link.id = id
	link.href = FIBR_PRECONNECT_URL
	link.rel = rel
	document.head.appendChild(link)
}

function appendInlineLoader(): void {
	if (document.getElementById(FIBR_CLIENT_SDK_ID)) return
	if (document.getElementById(FIBR_INLINE_SCRIPT_ID)) return

	const script = document.createElement('script')
	script.dataset.testid = FIBR_INLINE_SCRIPT_ID
	script.id = FIBR_INLINE_SCRIPT_ID
	script.type = 'text/javascript'
	script.text = FIBR_INLINE_SCRIPT
	document.head.appendChild(script)
}

function removeElementById(id: string): void {
	document.getElementById(id)?.remove()
}

export function removeFibrArtifacts(): void {
	removeElementById(FIBR_INLINE_SCRIPT_ID)
	removeElementById(FIBR_CLIENT_SDK_ID)
	removeElementById(FIBR_PRECONNECT_LINK_ID)
	removeElementById(FIBR_DNS_PREFETCH_LINK_ID)

	FIBR_TEMP_STYLE_IDS.forEach(removeElementById)
}

interface FibrCoordinatorProps {
	pageUrl: string | undefined
	featuresList: {codename: string}[] | undefined
	locale: string | undefined
}

export function FibrCoordinator({
	pageUrl,
	featuresList,
	locale,
}: Readonly<FibrCoordinatorProps>): JSX.Element | null {
	const router = useRouter()
	const shouldLoadFibr = isFibrPilotPage({
		tower: env.NEXT_PUBLIC_TOWER,
		locale,
		pageUrl,
		featuresList,
	})

	useEffect(() => {
		if (!shouldLoadFibr) {
			removeFibrArtifacts()
			return
		}

		appendLink(FIBR_PRECONNECT_LINK_ID, 'preconnect')
		appendLink(FIBR_DNS_PREFETCH_LINK_ID, 'dns-prefetch')
		appendInlineLoader()
	}, [router.asPath, shouldLoadFibr])

	if (!shouldLoadFibr) return null

	return (
		<Head>
			<link
				data-testid={FIBR_PRECONNECT_LINK_ID}
				href={FIBR_PRECONNECT_URL}
				id={FIBR_PRECONNECT_LINK_ID}
				rel="preconnect"
			/>
			<link
				data-testid={FIBR_DNS_PREFETCH_LINK_ID}
				href={FIBR_PRECONNECT_URL}
				id={FIBR_DNS_PREFETCH_LINK_ID}
				rel="dns-prefetch"
			/>
		</Head>
	)
}
